import React, { createContext, useContext, ReactNode } from "react";
import { Theme, defaultTheme } from "./index";

interface ThemeContextValue {
  theme: Theme;
  updateTheme: (newTheme: Partial<Theme>) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  theme?: Partial<Theme>;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  theme: customTheme,
}) => {
  // Using useMemo instead of useState to prevent unnecessary re-renders
  const theme = React.useMemo<Theme>(
    () => ({
      ...defaultTheme,
      ...customTheme,
      colors: { ...defaultTheme.colors, ...customTheme?.colors },
      spacing: { ...defaultTheme.spacing, ...customTheme?.spacing },
      borderRadius: {
        ...defaultTheme.borderRadius,
        ...customTheme?.borderRadius,
      },
      typography: {
        ...defaultTheme.typography,
        ...customTheme?.typography,
        fontSize: {
          ...defaultTheme.typography.fontSize,
          ...customTheme?.typography?.fontSize,
        },
        fontWeight: {
          ...defaultTheme.typography.fontWeight,
          ...customTheme?.typography?.fontWeight,
        },
        lineHeight: {
          ...defaultTheme.typography.lineHeight,
          ...customTheme?.typography?.lineHeight,
        },
      },
    }),
    [customTheme]
  );

  const updateTheme = React.useCallback((newTheme: Partial<Theme>) => {
    console.log(
      "Theme update requested, but we are using static theme for now.",
      newTheme
    );
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, updateTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
